import React, { useMemo, useState } from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRoute } from "@react-navigation/native";

import Container from "../../components/Container";
import Header from "../../components/Header";
import Text from "../../components/Text";
import Button from "../../components/Button";
import StickyFooter from "../../components/StickyFooter";
import ServiceList from "../../components/ServiceList";
import { colors, fonts } from "../../theme";

const initials = (name?: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const styles = StyleSheet.create({
  container: { backgroundColor: colors.black, flex: 1 },
  scrollView: { paddingHorizontal: 20 },
  sectionLabel: {
    fontFamily: "RobotoMono_400Regular",
    color: colors.midGrey,
    textTransform: "uppercase",
    fontSize: 11,
    letterSpacing: 1,
    marginTop: 10,
    marginBottom: 10,
  },
  artistPill: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#262b42",
    borderWidth: 1.5,
    borderColor: "#2a2d3f",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  artistPill__selected: { borderColor: colors.yellow },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.darkestGrey,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  avatarText: { fontFamily: "RobotoMono_400Regular", fontSize: 10, color: colors.midGrey },
  artistName: { flex: 1, color: colors.white, fontSize: 13 },
  radioDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: colors.darkestGrey,
  },
  radioDot__selected: { borderColor: colors.yellow, backgroundColor: colors.yellow },
});

type Props = { navigation: any };

const ServiceSelectionScreen: React.FC<Props> = ({ navigation }) => {
  const { params } = useRoute<any>();
  const { store, serviceType, serviceTypeCategory } = params || {};
  const services = serviceTypeCategory?.services || [];

  // A category with only one service (common -- Navel, Cheek, Nipple, etc.
  // are each just one procedure) doesn't need its own picking step.
  const [selectedService, setSelectedService] = useState<any>(
    services.length === 1 ? services[0] : null,
  );
  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null);

  const artists = useMemo(
    () => (store?.artists?.edges || []).map((edge: any) => edge.node),
    [store],
  );

  const qualifiedArtists = useMemo(
    () => artists.filter((artist: any) => (artist.services || []).includes(serviceType?.type)),
    [artists, serviceType],
  );

  const handleSelectService = (service: any) => {
    setSelectedService(service);
    setSelectedArtistId(null);
  };

  const handleNext = () => {
    navigation.navigate("DateTimeSelection", {
      store,
      service: { ...selectedService, serviceType: serviceType?.type },
      employeeId: selectedArtistId,
    });
  };

  return (
    <SafeAreaView style={styles.container} edges={["right", "bottom", "left"]}>
      <Container>
        <ScrollView style={styles.scrollView}>
          <Header
            title={serviceTypeCategory?.name}
            description="Select the specific service you'd like."
          />

          {services.length > 1 && (
            <ServiceList
              serviceType={serviceType}
              services={services}
              selectedService={selectedService}
              onSelect={handleSelectService}
            />
          )}

          {selectedService && (
            <>
              <Text style={styles.sectionLabel}>Artist</Text>
              <TouchableOpacity
                style={[
                  styles.artistPill,
                  selectedArtistId === null && styles.artistPill__selected,
                ]}
                onPress={() => setSelectedArtistId(null)}
              >
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>ANY</Text>
                </View>
                <Text style={styles.artistName}>First available artist</Text>
                <View
                  style={[
                    styles.radioDot,
                    selectedArtistId === null && styles.radioDot__selected,
                  ]}
                />
              </TouchableOpacity>
              {qualifiedArtists.map((artist: any) => {
                const selected = selectedArtistId === artist.id;
                return (
                  <TouchableOpacity
                    key={artist.id}
                    style={[styles.artistPill, selected && styles.artistPill__selected]}
                    onPress={() => setSelectedArtistId(artist.id)}
                  >
                    <View style={styles.avatar}>
                      <Text style={styles.avatarText}>{initials(artist.person?.name)}</Text>
                    </View>
                    <Text style={styles.artistName}>{artist.person?.name}</Text>
                    <View style={[styles.radioDot, selected && styles.radioDot__selected]} />
                  </TouchableOpacity>
                );
              })}
            </>
          )}
        </ScrollView>
      </Container>
      <StickyFooter>
        <Button title="Continue" onPress={handleNext} disabled={!selectedService} />
      </StickyFooter>
    </SafeAreaView>
  );
};

export default ServiceSelectionScreen;
